import { type ReactNode, useCallback, useLayoutEffect, useRef, useState } from "react";

interface SectionActionsProps {
	/** The section's own buttons, unchanged. */
	children: ReactNode;
	/** The row's layout classes — `flex items-center justify-between` and friends. */
	className?: string;
}

interface Column {
	left: number;
	width: number;
	height: number;
}

const SAME = (a: Column, b: Column) => a.left === b.left && a.width === b.width && a.height === b.height;

/**
 * The bar carrying a section's Previous/Next (or Skip/Continue) buttons, pinned
 * to the bottom of the viewport so the way forward is on screen whatever the
 * section's length.
 *
 * Pinned means fixed, and a fixed bar is out of the flow, so nothing sizes it —
 * which is why it has to measure. It leaves an anchor behind in the flow, takes
 * that anchor's width and left edge, and matches them. The anchor is an ordinary
 * block, so its width is the section's own: the bar comes out exactly as wide as
 * the form it belongs to, whether that section fills the shell or caps itself at
 * `md:w-2/3` like education, and follows it when the window changes.
 *
 * Restating the shell's geometry instead was the other option, and it cannot work
 * — the shell does not know what width a section chose for itself.
 *
 * The anchor also holds the bar's own height open at the end of the section, so
 * the last field is not left underneath it.
 *
 * `bottom-9` clears the mobile progress-dot strip so the two stack instead of
 * overlapping; from `md` those dots are hidden and the bar sits on the floor.
 */
const SectionActions = ({ children, className }: SectionActionsProps) => {
	const anchorRef = useRef<HTMLDivElement>(null);
	const barRef = useRef<HTMLDivElement>(null);
	const [column, setColumn] = useState<Column>({ left: 0, width: 0, height: 0 });

	const measure = useCallback(() => {
		const anchor = anchorRef.current;
		const bar = barRef.current;
		if (!anchor || !bar) return;

		const { left, width } = anchor.getBoundingClientRect();
		const next = { left, width, height: bar.offsetHeight };

		setColumn((current) => (SAME(current, next) ? current : next));
	}, []);

	useLayoutEffect(() => {
		const anchor = anchorRef.current;
		const bar = barRef.current;
		if (!anchor || !bar) return;

		measure();

		const observer = new ResizeObserver(measure);
		observer.observe(anchor);
		observer.observe(bar);
		window.addEventListener("resize", measure);

		return () => {
			observer.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, [measure]);

	return (
		<div ref={anchorRef} className="mt-6" style={{ height: column.height }}>
			<div
				ref={barRef}
				className="fixed bottom-9 z-40 border-gray-200 border-t bg-white py-3 md:bottom-0"
				style={{ left: column.left, width: column.width }}
			>
				<div className={className}>{children}</div>
			</div>
		</div>
	);
};

export default SectionActions;
